
import React, { useState, useRef, useEffect } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  ArrowLeft, Droplets, Pill, Syringe, AlertTriangle,
  Send, Bot, BookOpen, ChevronDown, ChevronUp, Clock, Activity, Wind, FlaskConical
} from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { sendInfirmierMessage } from '../../Services/infirmierChatService';
import { useAuth } from '../../contexts/AuthContext';
import logRecentSearch from './utils/logRecentSearch';

// ─── Helpers ──────────────────────────────────────────
function round2(n) {
  return isNaN(n) || !isFinite(n) ? null : Math.round(n * 100) / 100;
}

// ─── Contextes IA par calculateur ─────────────────────
const CHAT_CONTEXTS = {
  perfusion: [
    { role: 'user', content: "Contexte : je travaille avec un calculateur de débit de perfusion. Je peux avoir des questions sur les formules, les facteurs de perfusion, ou des cas cliniques liés aux perfusions IV." },
    { role: 'assistant', content: "Compris. Je suis disponible pour toutes vos questions sur les débits de perfusion. Posez votre question." },
  ],
  dose: [
    { role: 'user', content: "Contexte : je travaille avec un calculateur de dose médicamenteuse (dose fixe ou au poids, reconstitution d'ampoule, volume à prélever). Je peux avoir des questions sur les doses usuelles, les marges thérapeutiques, ou des ajustements selon l'âge/poids." },
    { role: 'assistant', content: "Compris. Je suis disponible pour toutes vos questions sur le calcul de doses et la reconstitution des médicaments. Posez votre question." },
  ],
  pse: [
    { role: 'user', content: "Contexte : je travaille avec un calculateur de PSE (pousse-seringue électrique) incluant la préparation de la seringue. Je peux avoir des questions sur les débits PSE, les médicaments courants (dopamine, noradrénaline, insuline…), les concentrations, ou les recettes de préparation." },
    { role: 'assistant', content: "Compris. Je suis disponible pour vos questions sur les PSE et la préparation des seringues. Posez votre question." },
  ],
  clairance: [
    { role: 'user', content: "Contexte : je travaille avec un calculateur de clairance de la créatinine (Cockcroft-Gault). Je peux avoir des questions sur l'insuffisance rénale, l'adaptation des doses de médicaments, ou les limites de la formule." },
    { role: 'assistant', content: "Compris. Je suis prêt à vous aider concernant l'adaptation posologique et la fonction rénale. Posez votre question." },
  ],
  oxygene: [
    { role: 'user', content: "Contexte : je calcule l'autonomie d'une bouteille d'oxygène pour un transfert. Je peux avoir des questions sur l'oxygénothérapie, les débits, ou les précautions avec l'O2." },
    { role: 'assistant', content: "Compris. Je suis disponible pour vos questions sur l'oxygénothérapie et les transferts. Posez votre question." },
  ]
};

// ─── Aide-mémoire par onglet ──────────────────────────
const AIDE_MEMOIRE = {
  perfusion: {
    title: 'Aide-mémoire — Perfusion',
    sections: [
      {
        subtitle: 'Équivalences gouttes / ml',
        items: [
          'Cristalloïdes / Glucose : 20 gttes = 1 ml (tubulure standard)',
          'Sang / Produits sanguins (PSL) : 15 gttes = 1 ml',
          'Pédiatrie / Micro-perfuseur : 60 gttes = 1 ml',
        ]
      },
      {
        subtitle: 'Formule & conversion rapide',
        items: [
          'Gttes/min = (Volume ml × Facteur) ÷ Durée min',
          'ml/h = Volume ml ÷ Durée h',
          'ml/h ÷ 3 ≈ gttes/min (tubulure standard 20 gttes/ml)',
          'Débit max VVP recommandé : 500 ml/h',
        ]
      },
    ],
  },
  dose: {
    title: 'Aide-mémoire — Calcul de doses',
    sections: [
      {
        subtitle: 'Règle de trois (volume à prélever)',
        items: [
          'Volume (ml) = [Dose totale × Volume ampoule] ÷ Dosage ampoule',
          'Ex : 300 mg prescrits — ampoule 500 mg/10 ml → prélever 6 ml',
        ]
      },
      {
        subtitle: 'Repères usuels adulte',
        items: [
          'Paracétamol : 1 g/6h, max 4 g/j (2 g/j si insuffisance hépatique/rénale sévère)',
          'Morphine IV : 0,1 mg/kg en titration — demi-dose si > 75 ans',
          'Amoxicilline : 1 g/8h IV — adapter si clairance < 30 ml/min',
        ]
      },
      {
        subtitle: 'Sécurité',
        items: [
          'Utiliser le poids idéal pour les médicaments à marge thérapeutique étroite',
          'Toujours vérifier l\'unité : mg ≠ µg (facteur 1 000 !)',
          'Adapter systématiquement si insuffisance rénale ou hépatique',
        ]
      },
    ],
  },
  pse: {
    title: 'Aide-mémoire — PSE / Pousse-Seringue',
    sections: [
      {
        subtitle: 'Recettes standards fréquentes (50 ml)',
        items: [
          'Noradrénaline : 4 amp. de 2 mg/ml (8 mg total) dans 48 ml NaCl → 0,16 mg/ml',
          'Adrénaline : 5 amp. de 1 mg/ml (5 mg total) dans 45 ml NaCl → 0,1 mg/ml',
          'Insuline rapide : 50 UI dans 50 ml NaCl 0,9% → 1 UI/ml',
          'Héparine : 25 000 UI dans 50 ml NaCl → 500 UI/ml',
          'Morphine : 20 mg dans 20 ml NaCl 0,9% → 1 mg/ml',
        ]
      },
      {
        subtitle: 'Formule débit PSE (Gammas)',
        items: [
          'Débit (ml/h) = [Dose µg/kg/min × Poids kg × 60] ÷ [Concentration mg/ml × 1000]',
        ]
      },
      {
        subtitle: 'Surveillance',
        items: [
          'Tracer chaque changement de seringue dans le dossier de soins',
          'Ne jamais interrompre brutalement les catécholamines (rebond hémodynamique)',
          'Protéger de la lumière les ampoules de catécholamines',
          'Purger complètement avant branchement (bolus involontaire)',
        ]
      },
    ],
  },
  clairance: {
    title: 'Aide-mémoire — Fonction Rénale',
    sections: [
      {
        subtitle: 'Stades Insuffisance Rénale (KDIGO)',
        items: [
          'Normale : > 90 ml/min',
          'IR Légère (G2) : 60–89 ml/min',
          'IR Modérée (G3a/b) : 30–59 ml/min',
          'IR Sévère (G4) : 15–29 ml/min',
          'IR Terminale (G5) : < 15 ml/min',
        ]
      },
      {
        subtitle: 'Précautions',
        items: [
          'Cockcroft surestime souvent la clairance chez la personne âgée/dénutrie',
          'Attention aux AINS, aminosides, produits de contraste et IPP à forte dose',
          'Surveiller la diurèse ≥ 0,5 ml/kg/h comme signe de perfusion rénale',
        ]
      },
    ],
  },
  oxygene: {
    title: 'Aide-mémoire — Oxygène',
    sections: [
      {
        subtitle: 'Tailles standards des bouteilles',
        items: [
          'Bouteille de 0,4 L (poche néonatale) — pression 200 bar',
          'Bouteille de 2 L (urgences légères) — pression 200 bar',
          'Bouteille de 5 L (transport standard) — pression 200 bar',
          'Bouteille de 15 L (réserve, urgences) — pression 200 bar',
        ]
      },
      {
        subtitle: 'Consignes de sécurité',
        items: [
          'Toujours garder une marge de 10–15 min de sécurité pour tout transfert',
          'Ne jamais graisser les robinets/manomètres (risque d\'inflammation O2)',
          'Stockage : debout, fixée, loin de sources de chaleur (> 50 °C)',
        ]
      },
    ],
  }
};

// ─── Composants d'interface (Génériques) ──────────────
function InputField({ label, value, onChange, unit, placeholder = '0', type = 'number' }) {
  return (
    <div className="flex flex-col gap-1">
      <label className="text-sm font-medium text-slate-600 dark:text-slate-400">{label}</label>
      <div className="flex items-center gap-2">
        <input
          type={type}
          min={type === 'number' ? 0 : undefined}
          step="any"
          value={value}
          onChange={e => onChange(e.target.value)}
          placeholder={placeholder}
          className="w-full rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-400 dark:focus:ring-teal-500 transition"
        />
        {unit && (
          <span className="min-w-[52px] text-right text-sm text-slate-500 dark:text-slate-400 whitespace-nowrap">
            {unit}
          </span>
        )}
      </div>
    </div>
  );
}

function ResultBox({ children }) {
  return (
    <div className="mt-4 rounded-xl bg-teal-50 dark:bg-teal-900/20 border border-teal-200 dark:border-teal-700 p-4 space-y-2">
      {children}
    </div>
  );
}

function ResultLine({ label, value, unit, highlight = false }) {
  return (
    <div className="flex items-baseline justify-between gap-4">
      <span className="text-sm text-slate-600 dark:text-slate-400">{label}</span>
      <span className={`text-2xl font-bold tabular-nums ${highlight ? 'text-amber-600 dark:text-amber-400' : 'text-teal-700 dark:text-teal-300'}`}>
        {value !== null
          ? <>{value} <span className="text-base font-semibold">{unit}</span></>
          : <span className="text-lg font-normal text-slate-300 dark:text-slate-600">—</span>}
      </span>
    </div>
  );
}

function AlertVigilance({ message }) {
  return (
    <div className="flex items-start gap-2.5 px-4 py-3 rounded-xl bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700/50">
      <AlertTriangle size={15} className="shrink-0 mt-0.5 text-amber-500" />
      <p className="text-xs text-amber-700 dark:text-amber-300 leading-relaxed">
        <span className="font-semibold">Point de vigilance : </span>{message}
      </p>
    </div>
  );
}

function SectionLabel({ children }) {
  return (
    <p className="text-[11px] font-bold text-teal-600 dark:text-teal-400 uppercase tracking-wider">
      {children}
    </p>
  );
}

function CalcHistory({ entries }) {
  if (entries.length === 0) return null;
  return (
    <div className="rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden">
      <div className="flex items-center gap-2 px-3 py-2 bg-slate-50 dark:bg-slate-800/80 border-b border-slate-200 dark:border-slate-700">
        <Clock size={13} className="text-slate-400" />
        <span className="text-xs font-semibold text-slate-500 dark:text-slate-400">Derniers calculs</span>
      </div>
      <div className="divide-y divide-slate-100 dark:divide-slate-800">
        {entries.map((e, i) => (
          <div key={i} className="px-3 py-2.5 flex items-center justify-between gap-3">
            <div className="flex-1 min-w-0">
              <p className="text-xs text-slate-500 dark:text-slate-400 truncate">{e.params}</p>
              <p className="text-xs font-semibold text-teal-600 dark:text-teal-400 mt-0.5">{e.result}</p>
            </div>
            <span className="text-xs text-slate-300 dark:text-slate-600 shrink-0">
              {e.ts.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function MemoCard({ tabId }) {
  const [open, setOpen] = useState(false);
  const data = AIDE_MEMOIRE[tabId];

  return (
    <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm overflow-hidden">
      <button
        onClick={() => setOpen(v => !v)}
        className="w-full flex items-center justify-between px-5 py-3.5 hover:bg-slate-50 dark:hover:bg-slate-800/50 transition"
      >
        <div className="flex items-center gap-2.5">
          <BookOpen size={15} className="text-teal-500" />
          <span className="text-sm font-semibold text-slate-700 dark:text-slate-300">{data.title}</span>
        </div>
        {open ? <ChevronUp size={16} className="text-slate-400" /> : <ChevronDown size={16} className="text-slate-400" />}
      </button>
      {open && (
        <div className="px-5 pb-5 space-y-4 border-t border-slate-100 dark:border-slate-800">
          {data.sections.map((section, i) => (
            <div key={i} className="pt-4">
              <p className="text-[11px] font-bold text-teal-600 dark:text-teal-400 uppercase tracking-wider mb-2">{section.subtitle}</p>
              <ul className="space-y-1.5">
                {section.items.map((item, j) => (
                  <li key={j} className="flex items-start gap-2 text-sm text-slate-600 dark:text-slate-300">
                    <span className="text-teal-400 dark:text-teal-500 mt-0.5 shrink-0 font-bold">•</span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Mini Chat IA ──────────────────────────────────────
function MiniChat({ tabId }) {
  const contextSeed = CHAT_CONTEXTS[tabId] || CHAT_CONTEXTS['perfusion'];
  const [messages, setMessages] = useState([]);
  const [conversationId, setConvId] = useState(null);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const messagesRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (messages.length === 0) return;
    const el = messagesRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, loading]);

  async function handleSend() {
    const text = input.trim();
    if (!text || loading) return;
    const userMsg = { role: 'user', content: text };
    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setLoading(true);
    try {
      const history = [...contextSeed, ...messages, userMsg];
      const data = await sendInfirmierMessage(text, conversationId, history.slice(-12));
      if (!conversationId && data.conversationId) setConvId(data.conversationId);
      setMessages(prev => [...prev, { role: 'assistant', content: data.response }]);
    } catch {
      setMessages(prev => [...prev, { role: 'assistant', content: "Une erreur est survenue. Veuillez réessayer." }]);
    } finally {
      setLoading(false);
      inputRef.current?.focus();
    }
  }

  function handleKey(e) {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); }
  }

  return (
    <div className="mt-5 border-t border-slate-100 dark:border-slate-800 pt-5">
      <div className="flex items-center gap-2 mb-3">
        <Bot size={15} className="text-teal-500" />
        <span className="text-sm font-semibold text-slate-700 dark:text-slate-300">
          Assistant IA — {tabId.charAt(0).toUpperCase() + tabId.slice(1)}
        </span>
      </div>
      <div className="bg-slate-50 dark:bg-slate-950 rounded-xl border border-slate-200 dark:border-slate-800 flex flex-col" style={{ minHeight: '300px', maxHeight: '720px' }}>
        <div ref={messagesRef} className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
          {messages.length === 0 && !loading && (
            <p className="text-sm text-slate-400 dark:text-slate-500 text-center mt-8">Posez une question sur ce calculateur…</p>
          )}
          {messages.map((msg, i) => (
            <div key={i} className={`flex gap-2.5 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
              {msg.role === 'assistant' && (
                <div className="shrink-0 w-7 h-7 rounded-full bg-teal-100 dark:bg-teal-900/40 flex items-center justify-center mt-0.5">
                  <Bot size={13} className="text-teal-600 dark:text-teal-400" />
                </div>
              )}
              <div className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed ${
                msg.role === 'user'
                  ? 'bg-teal-500 text-white rounded-tr-sm'
                  : 'bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-200 border border-slate-200 dark:border-slate-700 rounded-tl-sm'
              }`}>
                {msg.role === 'assistant' ? (
                  <div className="ai-message prose prose-sm dark:prose-invert max-w-none">
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>{msg.content}</ReactMarkdown>
                  </div>
                ) : msg.content}
              </div>
            </div>
          ))}
          {loading && (
            <div className="flex gap-2.5 justify-start">
              <div className="shrink-0 w-7 h-7 rounded-full bg-teal-100 dark:bg-teal-900/40 flex items-center justify-center">
                <Bot size={13} className="text-teal-600 dark:text-teal-400" />
              </div>
              <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-2xl rounded-tl-sm px-4 py-3 flex gap-1 items-center">
                {[0, 150, 300].map(delay => (
                  <span key={delay} className="w-1.5 h-1.5 bg-teal-400 rounded-full animate-bounce" style={{ animationDelay: `${delay}ms` }} />
                ))}
              </div>
            </div>
          )}
        </div>
        <div className="border-t border-slate-200 dark:border-slate-800 px-3 py-2.5 flex gap-2 items-end">
          <textarea
            ref={inputRef} value={input} onChange={e => setInput(e.target.value)} onKeyDown={handleKey}
            placeholder="Posez votre question…" rows={1}
            className="flex-1 resize-none bg-transparent text-sm text-slate-800 dark:text-slate-200 placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none"
            style={{ maxHeight: '80px', overflowY: 'auto' }}
          />
          <button onClick={handleSend} disabled={!input.trim() || loading}
            className="shrink-0 w-8 h-8 flex items-center justify-center rounded-full bg-teal-500 hover:bg-teal-600 disabled:opacity-40 disabled:cursor-not-allowed transition">
            <Send size={14} className="text-white" />
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Toggle Segment (réutilisable) ────────────────────
function SegmentToggle({ options, value, onChange }) {
  return (
    <div className="flex rounded-lg overflow-hidden border border-slate-200 dark:border-slate-700 text-sm w-fit">
      {options.map(opt => (
        <button
          key={opt.value}
          onClick={() => onChange(opt.value)}
          className={`px-4 py-2 font-medium transition ${
            value === opt.value
              ? 'bg-teal-500 text-white'
              : 'bg-white dark:bg-slate-800 text-slate-500 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-700'
          }`}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}


// ═══════════════════════════════════════════════════════
// CALCULATEUR 1 — DÉBIT DE PERFUSION (refactorisé)
// ═══════════════════════════════════════════════════════
const FACTEUR_PRESETS = [
  { label: 'Standard', value: '20', sub: '20 gttes/ml' },
  { label: 'Sang / PSL', value: '15', sub: '15 gttes/ml' },
  { label: 'Pédiatrie', value: '60', sub: '60 gttes/ml' },
];

function CalculateurPerfusion() {
  const { user } = useAuth();
  const [volume, setVolume] = useState('');
  const [duree, setDuree] = useState('');
  const [dureeUnit, setDureeUnit] = useState('h');
  const [facteur, setFacteur] = useState('20');
  const [history, setHistory] = useState([]);

  const v = parseFloat(volume);
  const d = parseFloat(duree);
  const f = parseFloat(facteur);

  let mlH = null;
  let gttMin = null;

  if (v > 0 && d > 0) {
    if (dureeUnit === 'h') {
      mlH = round2(v / d);
      gttMin = f > 0 ? round2((v / d) * f / 60) : null;
    } else {
      gttMin = f > 0 ? round2((v / d) * f) : null;
      mlH = round2((v / d) * 60);
    }
  }

  useEffect(() => {
    if (mlH === null) return;
    const timer = setTimeout(() => {
      setHistory(prev => {
        const result = gttMin !== null ? `${mlH} ml/h — ${gttMin} gttes/min` : `${mlH} ml/h`;
        if (prev.length > 0 && prev[0].result === result) return prev;
        return [{ params: `${volume} ml / ${duree} ${dureeUnit} — facteur ${facteur}`, result, ts: new Date() }, ...prev].slice(0, 3);
      });
    }, 1000);
    return () => clearTimeout(timer);
  }, [mlH, gttMin]);

  useEffect(() => {
    if (mlH === null) return;
    const timer = setTimeout(() => {
      const detail = gttMin !== null ? `${mlH} ml/h — ${gttMin} gttes/min` : `${mlH} ml/h`;
      logRecentSearch(user?.uid, { type: 'calcul', label: 'Débit de perfusion', detail }).catch(() => {});
    }, 1000);
    return () => clearTimeout(timer);
  }, [mlH, gttMin, user?.uid]);

  const alerts = [];
  if (volume !== '' && v === 0) alerts.push("Volume à perfuser à 0 ml — vérifiez la prescription avant de calculer.");
  if (gttMin !== null && gttMin > 60) alerts.push("Débit gravitaire élevé (> 60 gttes/min) — vérifiez la tolérance et le calibre de la voie d'accès.");
  if (mlH !== null && mlH > 500) alerts.push("Débit supérieur à 500 ml/h — vérifiez la prescription et la voie d'accès vasculaire.");

  return (
    <div className="space-y-5">
      {/* Volume + Durée */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <InputField label="Volume à perfuser" value={volume} onChange={setVolume} unit="ml" />

        {/* Durée avec toggle h / min intégré */}
        <div className="flex flex-col gap-1">
          <label className="text-sm font-medium text-slate-600 dark:text-slate-400">Durée</label>
          <div className="flex items-center gap-2">
            <input
              type="number" min={0} step="any"
              value={duree} onChange={e => setDuree(e.target.value)}
              placeholder="0"
              className="flex-1 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-400 transition"
            />
            <div className="flex rounded-lg overflow-hidden border border-slate-200 dark:border-slate-700 text-xs shrink-0">
              <button
                onClick={() => setDureeUnit('h')}
                className={`px-3 py-2 font-bold transition ${dureeUnit === 'h' ? 'bg-teal-500 text-white' : 'bg-white dark:bg-slate-800 text-slate-500 dark:text-slate-400'}`}
              >h</button>
              <button
                onClick={() => setDureeUnit('min')}
                className={`px-3 py-2 font-bold transition ${dureeUnit === 'min' ? 'bg-teal-500 text-white' : 'bg-white dark:bg-slate-800 text-slate-500 dark:text-slate-400'}`}
              >min</button>
            </div>
          </div>
        </div>
      </div>

      {/* Facteur tubulure */}
      <div className="space-y-2">
        <label className="text-sm font-medium text-slate-600 dark:text-slate-400">Type de tubulure</label>
        <div className="grid grid-cols-3 gap-2">
          {FACTEUR_PRESETS.map(preset => (
            <button
              key={preset.value}
              onClick={() => setFacteur(preset.value)}
              className={`flex flex-col items-center gap-0.5 px-3 py-2.5 rounded-xl border text-sm font-medium transition ${
                facteur === preset.value
                  ? 'bg-teal-500 text-white border-teal-500 shadow-sm shadow-teal-500/20'
                  : 'bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:border-teal-400'
              }`}
            >
              <span>{preset.label}</span>
              <span className={`text-[11px] ${facteur === preset.value ? 'text-teal-100' : 'text-slate-400 dark:text-slate-500'}`}>{preset.sub}</span>
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2 pt-1">
          <span className="text-xs text-slate-400 dark:text-slate-500 shrink-0">Ou valeur personnalisée :</span>
          <input
            type="number" min={0} step="1"
            value={facteur}
            onChange={e => setFacteur(e.target.value)}
            placeholder="ex: 20"
            className="w-20 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-teal-400 transition"
          />
          <span className="text-sm text-slate-400 dark:text-slate-500">gttes/ml</span>
        </div>
      </div>

      <ResultBox>
        <ResultLine label="Débit volumique" value={mlH} unit="ml/h" highlight />
        <div className="border-t border-teal-200 dark:border-teal-700/50 pt-2" />
        <ResultLine label="Débit gravitaire" value={gttMin} unit="gouttes/min" />
      </ResultBox>
      {alerts.map((a, i) => <AlertVigilance key={i} message={a} />)}
      <CalcHistory entries={history} />
      <MiniChat tabId="perfusion" />
    </div>
  );
}


// ═══════════════════════════════════════════════════════
// CALCULATEUR 2 — DOSE MÉDICAMENTEUSE (refactorisé)
// ═══════════════════════════════════════════════════════
const DOSE_UNITS = ['mg', 'g', 'µg', 'UI'];

function CalculateurDose() {
  const { user } = useAuth();
  const [prescType, setPrescType] = useState('poids');
  const [unit, setUnit] = useState('mg');
  const [dosePrescrite, setDosePrescrite] = useState('');
  const [poids, setPoids] = useState('');
  const [ampDosage, setAmpDosage] = useState('');
  const [ampVolume, setAmpVolume] = useState('');
  const [history, setHistory] = useState([]);

  const dp = parseFloat(dosePrescrite);
  const p = parseFloat(poids);
  const ad = parseFloat(ampDosage);
  const av = parseFloat(ampVolume);

  const doseTotale = (() => {
    if (prescType === 'fixe' && dp > 0) return round2(dp);
    if (prescType === 'poids' && dp > 0 && p > 0) return round2(dp * p);
    return null;
  })();

  // Règle de trois : Volume (ml) = [Dose totale × Volume ampoule] / Dosage ampoule
  const volumeAPrelevér = doseTotale !== null && ad > 0 && av > 0
    ? round2((doseTotale * av) / ad)
    : null;

  useEffect(() => {
    if (doseTotale === null) return;
    const timer = setTimeout(() => {
      setHistory(prev => {
        const result = volumeAPrelevér !== null
          ? `${doseTotale} ${unit} → ${volumeAPrelevér} ml`
          : `Dose totale : ${doseTotale} ${unit}`;
        if (prev.length > 0 && prev[0].result === result) return prev;
        const params = prescType === 'poids'
          ? `${dp} ${unit}/kg × ${p} kg`
          : `${dp} ${unit} (fixe)`;
        return [{ params, result, ts: new Date() }, ...prev].slice(0, 3);
      });
    }, 1000);
    return () => clearTimeout(timer);
  }, [doseTotale, volumeAPrelevér]);

  useEffect(() => {
    if (doseTotale === null) return;
    const timer = setTimeout(() => {
      const detail = volumeAPrelevér !== null
        ? `${doseTotale} ${unit} → ${volumeAPrelevér} ml`
        : `${doseTotale} ${unit}`;
      logRecentSearch(user?.uid, { type: 'calcul', label: 'Calcul de dose', detail }).catch(() => {});
    }, 1000);
    return () => clearTimeout(timer);
  }, [doseTotale, volumeAPrelevér, unit, user?.uid]);

  const alerts = [];
  if (prescType === 'poids' && poids !== '' && (p < 3 || p > 300)) {
    alerts.push("Poids patient inhabituel — vérifiez la valeur saisie (plage attendue : 3–300 kg).");
  }
  if (dosePrescrite !== '' && dp === 0) {
    alerts.push("Dose prescrite à 0 — vérifiez la prescription médicale avant de calculer.");
  }
  if (ampVolume !== '' && av === 0) {
    alerts.push("Volume ampoule à 0 ml — vérifiez les données du conditionnement.");
  }
  if (volumeAPrelevér !== null && av > 0 && volumeAPrelevér > av) {
    alerts.push(`Volume à prélever (${volumeAPrelevér} ml) supérieur au volume de l'ampoule (${av} ml) — vérifiez le dosage ou utilisez plusieurs ampoules.`);
  }
  if (unit === 'µg' && dp > 0 && prescType === 'fixe' && dp >= 1000) {
    alerts.push("Dose en µg ≥ 1000 µg — vérifiez si l'unité correcte est mg (1000 µg = 1 mg).");
  }

  return (
    <div className="space-y-5">

      {/* Type de prescription */}
      <div className="space-y-2">
        <SectionLabel>Type de prescription</SectionLabel>
        <SegmentToggle
          options={[
            { value: 'poids', label: 'Dose au poids (par kg)' },
            { value: 'fixe', label: 'Dose fixe' },
          ]}
          value={prescType}
          onChange={setPrescType}
        />
      </div>

      {/* Unité */}
      <div className="space-y-2">
        <SectionLabel>Unité de dose</SectionLabel>
        <div className="flex flex-wrap gap-2">
          {DOSE_UNITS.map(u => (
            <button
              key={u}
              onClick={() => setUnit(u)}
              className={`px-4 py-1.5 rounded-lg text-sm font-semibold border transition ${
                unit === u
                  ? 'bg-teal-500 text-white border-teal-500 shadow-sm'
                  : 'bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:border-teal-400'
              }`}
            >
              {u}
            </button>
          ))}
        </div>
      </div>

      {/* Prescription */}
      <div className="space-y-3">
        <SectionLabel>Prescription médicale</SectionLabel>
        <div className={`grid gap-4 ${prescType === 'poids' ? 'grid-cols-1 sm:grid-cols-2' : 'grid-cols-1 sm:grid-cols-2'}`}>
          <InputField
            label="Dose prescrite"
            value={dosePrescrite}
            onChange={setDosePrescrite}
            unit={prescType === 'poids' ? `${unit}/kg` : unit}
          />
          {prescType === 'poids' && (
            <InputField label="Poids du patient" value={poids} onChange={setPoids} unit="kg" />
          )}
        </div>
      </div>

      {/* Conditionnement */}
      <div className="space-y-3">
        <SectionLabel>Conditionnement — Ampoule / Flacon</SectionLabel>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <InputField
            label="Dosage de l'ampoule"
            value={ampDosage}
            onChange={setAmpDosage}
            unit={unit}
            placeholder="ex: 500"
          />
          <InputField
            label="Volume de l'ampoule / solvant"
            value={ampVolume}
            onChange={setAmpVolume}
            unit="ml"
            placeholder="ex: 10"
          />
        </div>
      </div>

      {/* Résultats */}
      <ResultBox>
        <ResultLine label="Dose totale requise" value={doseTotale} unit={unit} />
        <div className="border-t border-teal-200 dark:border-teal-700/50 pt-2" />
        <ResultLine label="Volume exact à prélever" value={volumeAPrelevér} unit="ml" highlight />
      </ResultBox>

      {alerts.map((a, i) => <AlertVigilance key={i} message={a} />)}
      <CalcHistory entries={history} />
      <MiniChat tabId="dose" />
    </div>
  );
}


// ═══════════════════════════════════════════════════════
// CALCULATEUR 3 — PSE / POUSSE-SERINGUE (refactorisé)
// ═══════════════════════════════════════════════════════
function CalculateurPSE() {
  const { user } = useAuth();
  const [dose, setDose] = useState('');
  const [poids, setPoids] = useState('');
  const [ampDosage, setAmpDosage] = useState('');
  const [ampVolume, setAmpVolume] = useState('');
  const [nbAmpoules, setNbAmpoules] = useState('1');
  const [serVolume, setSerVolume] = useState('50');

  const d = parseFloat(dose);
  const p = parseFloat(poids);
  const ad = parseFloat(ampDosage);
  const av = parseFloat(ampVolume);
  const na = parseFloat(nbAmpoules);
  const sv = parseFloat(serVolume);

  // Concentration résultante dans la seringue (mg/ml)
  const concSer = ad > 0 && na > 0 && sv > 0 ? round2((ad * na) / sv) : null;

  // Débit PSE (ml/h) = [dose µg/kg/min × poids × 60] / [concentration mg/ml × 1000]
  const debitPSE = d > 0 && p > 0 && concSer !== null && concSer > 0
    ? round2((d * p * 60) / (concSer * 1000))
    : null;

  // Préparation seringue
  const volProduit = av > 0 && na > 0 ? round2(av * na) : null;
  const volSolvant = volProduit !== null && sv > 0 ? round2(sv - volProduit) : null;
  const prepValide = volSolvant !== null && volSolvant >= 0;

  useEffect(() => {
    if (debitPSE === null) return;
    const timer = setTimeout(() => {
      logRecentSearch(user?.uid, { type: 'calcul', label: 'Débit PSE', detail: `${debitPSE} ml/h` }).catch(() => {});
    }, 1000);
    return () => clearTimeout(timer);
  }, [debitPSE, user?.uid]);

  const alerts = [];
  if (poids !== '' && (p < 3 || p > 300)) {
    alerts.push("Poids patient inhabituel — vérifiez la valeur saisie (plage attendue : 3–300 kg).");
  }
  if (dose !== '' && d === 0) {
    alerts.push("Dose prescrite à 0 µg/kg/min — vérifiez la prescription médicale avant de calculer.");
  }
  if (ampVolume !== '' && av === 0) {
    alerts.push("Volume ampoule à 0 ml — vérifiez les données du conditionnement.");
  }
  if (volSolvant !== null && volSolvant < 0) {
    alerts.push(`Volume de produit (${volProduit} ml) supérieur au volume cible de la seringue (${sv} ml) — réduisez le nombre d'ampoules ou augmentez le volume cible.`);
  }
  if (debitPSE !== null && debitPSE < 0.5) {
    alerts.push("Débit très faible (< 0,5 ml/h) — risque d'obstruction. Envisagez une concentration plus faible ou vérifiez la dose.");
  }
  if (debitPSE !== null && debitPSE > 20) {
    alerts.push("Débit élevé (> 20 ml/h) — vérifiez la concentration de la seringue ou augmentez le nombre d'ampoules.");
  }

  return (
    <div className="space-y-5">

      {/* Prescription */}
      <div className="space-y-3">
        <SectionLabel>Prescription médicale</SectionLabel>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <InputField label="Dose prescrite" value={dose} onChange={setDose} unit="µg/kg/min" placeholder="ex: 0.1" />
          <InputField label="Poids du patient" value={poids} onChange={setPoids} unit="kg" />
        </div>
      </div>

      {/* Préparation */}
      <div className="space-y-4">
        <SectionLabel>Préparation de la seringue</SectionLabel>

        {/* Ampoule disponible */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <InputField
            label="Dosage de l'ampoule disponible"
            value={ampDosage}
            onChange={setAmpDosage}
            unit="mg/amp."
            placeholder="ex: 8"
          />
          <InputField
            label="Volume de l'ampoule"
            value={ampVolume}
            onChange={setAmpVolume}
            unit="ml/amp."
            placeholder="ex: 2"
          />
        </div>

        {/* Nb ampoules + Volume seringue */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="flex flex-col gap-2">
            <label className="text-sm font-medium text-slate-600 dark:text-slate-400">Nombre d'ampoules à utiliser</label>
            <div className="flex rounded-lg overflow-hidden border border-slate-200 dark:border-slate-700 text-sm w-fit">
              {['1', '2', '3', '4'].map(n => (
                <button
                  key={n}
                  onClick={() => setNbAmpoules(n)}
                  className={`px-4 py-2 font-semibold transition ${
                    nbAmpoules === n
                      ? 'bg-teal-500 text-white'
                      : 'bg-white dark:bg-slate-800 text-slate-500 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-700'
                  }`}
                >
                  {n}
                </button>
              ))}
            </div>
          </div>

          <div className="flex flex-col gap-2">
            <label className="text-sm font-medium text-slate-600 dark:text-slate-400">Volume total de la seringue</label>
            <div className="flex rounded-lg overflow-hidden border border-slate-200 dark:border-slate-700 text-sm w-fit">
              {['20', '50'].map(vol => (
                <button
                  key={vol}
                  onClick={() => setSerVolume(vol)}
                  className={`px-4 py-2 font-semibold transition ${
                    serVolume === vol
                      ? 'bg-teal-500 text-white'
                      : 'bg-white dark:bg-slate-800 text-slate-500 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-700'
                  }`}
                >
                  {vol} ml
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Recette de préparation */}
      {prepValide && volProduit !== null && volSolvant !== null && (
        <div className="rounded-xl bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700/60 p-4 space-y-2.5">
          <div className="flex items-center gap-2">
            <FlaskConical size={14} className="text-blue-500 shrink-0" />
            <p className="text-[11px] font-bold text-blue-600 dark:text-blue-400 uppercase tracking-wider">
              Recette de préparation
            </p>
          </div>

          <div className="space-y-1.5">
            <p className="text-sm text-blue-800 dark:text-blue-200">
              <span className="font-bold">{na} ampoule{na > 1 ? 's' : ''}</span> ×{' '}
              {av > 0 ? `${av} ml` : '? ml'} = Prélever{' '}
              <span className="font-bold text-blue-900 dark:text-white">{volProduit} ml</span> de produit actif
            </p>
            <p className="text-sm text-blue-800 dark:text-blue-200">
              + Ajouter{' '}
              <span className="font-bold text-blue-900 dark:text-white">{volSolvant} ml</span>{' '}
              de solvant (NaCl 0,9% ou G5%)
            </p>
          </div>

          {concSer !== null && (
            <div className="flex flex-wrap gap-3 pt-1 border-t border-blue-200 dark:border-blue-700/40">
              <span className="text-xs text-blue-600 dark:text-blue-300">
                Volume total :{' '}
                <strong className="text-blue-800 dark:text-blue-100">{sv} ml</strong>
              </span>
              <span className="text-blue-300 dark:text-blue-600">·</span>
              <span className="text-xs text-blue-600 dark:text-blue-300">
                Concentration :{' '}
                <strong className="text-blue-800 dark:text-blue-100">{concSer} mg/ml</strong>
              </span>
              {ad > 0 && av > 0 && (
                <>
                  <span className="text-blue-300 dark:text-blue-600">·</span>
                  <span className="text-xs text-blue-600 dark:text-blue-300">
                    {na} amp. × {ad} mg = <strong className="text-blue-800 dark:text-blue-100">{round2(na * ad)} mg</strong> au total
                  </span>
                </>
              )}
            </div>
          )}
        </div>
      )}

      {/* Résultat débit */}
      <ResultBox>
        {d > 0 && p > 0 && (
          <ResultLine label="Dose prescrite" value={d} unit="µg/kg/min" />
        )}
        {d > 0 && p > 0 && (
          <div className="border-t border-teal-200 dark:border-teal-700/50 pt-2" />
        )}
        <ResultLine label="Débit PSE calculé" value={debitPSE} unit="ml/h" highlight />
      </ResultBox>

      {alerts.map((a, i) => <AlertVigilance key={i} message={a} />)}
      <MiniChat tabId="pse" />
    </div>
  );
}


// ─── Calculateur 4 : Clairance Créatinine ─────────────
function CalculateurClairance() {
  const { user } = useAuth();
  const [sexe, setSexe] = useState('H');
  const [age, setAge] = useState('');
  const [poids, setPoids] = useState('');
  const [creat, setCreat] = useState('');

  const a = parseFloat(age); const p = parseFloat(poids); const c = parseFloat(creat);

  let clairance = null;
  if (a > 0 && p > 0 && c > 0) {
    clairance = ((140 - a) * p) / (0.814 * c);
    if (sexe === 'F') clairance = clairance * 0.85;
    clairance = round2(clairance);
  }

  useEffect(() => {
    if (clairance === null) return;
    const timer = setTimeout(() => {
      logRecentSearch(user?.uid, { type: 'calcul', label: 'Clairance (Cockcroft-Gault)', detail: `${clairance} ml/min` }).catch(() => {});
    }, 1000);
    return () => clearTimeout(timer);
  }, [clairance, user?.uid]);

  const alerts = [];
  if (clairance !== null) {
    if (clairance < 15) alerts.push("Insuffisance rénale terminale (stade G5). Adaptation drastique ou contre-indication de nombreux médicaments.");
    else if (clairance < 30) alerts.push("Insuffisance rénale sévère (G4). Adaptation posologique impérative — consulter le Vidal.");
    else if (clairance < 60) alerts.push("Insuffisance rénale modérée (G3). Vérifiez les recommandations posologiques pour chaque médicament.");
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <span className="text-sm font-medium text-slate-600 dark:text-slate-400">Sexe :</span>
        <SegmentToggle
          options={[{ value: 'H', label: 'Homme' }, { value: 'F', label: 'Femme' }]}
          value={sexe}
          onChange={setSexe}
        />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <InputField label="Âge" value={age} onChange={setAge} unit="ans" />
        <InputField label="Poids" value={poids} onChange={setPoids} unit="kg" />
        <InputField label="Créatininémie" value={creat} onChange={setCreat} unit="µmol/L" />
      </div>
      <ResultBox>
        <ResultLine label="Clairance (Cockcroft-Gault)" value={clairance} unit="ml/min" highlight={clairance !== null && clairance < 60} />
      </ResultBox>
      {alerts.map((a, i) => <AlertVigilance key={i} message={a} />)}
      <MiniChat tabId="clairance" />
    </div>
  );
}

// ─── Calculateur 5 : Autonomie Oxygène ────────────────
function CalculateurO2() {
  const { user } = useAuth();
  const [volumeBouteille, setVolumeBouteille] = useState('5');
  const [pression, setPression] = useState('');
  const [debit, setDebit] = useState('');

  const v = parseFloat(volumeBouteille);
  const p = parseFloat(pression);
  const d = parseFloat(debit);

  let autonomieMin = null;
  let autonomieAffichee = null;

  if (v > 0 && p > 0 && d > 0) {
    autonomieMin = (v * p) / d;
    const heures = Math.floor(autonomieMin / 60);
    const minutes = Math.floor(autonomieMin % 60);
    autonomieAffichee = heures > 0 ? `${heures}h ${minutes}min` : `${minutes} min`;
  }

  useEffect(() => {
    if (autonomieMin === null) return;
    const timer = setTimeout(() => {
      logRecentSearch(user?.uid, { type: 'calcul', label: 'Autonomie O2', detail: autonomieAffichee }).catch(() => {});
    }, 1000);
    return () => clearTimeout(timer);
  }, [autonomieMin, autonomieAffichee, user?.uid]);

  const alerts = [];
  if (autonomieMin !== null && autonomieMin < 30) {
    alerts.push("Autonomie faible (< 30 min). Prévoyez impérativement une bouteille de secours pour tout transfert.");
  }

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <InputField label="Volume bouteille" value={volumeBouteille} onChange={setVolumeBouteille} unit="L" placeholder="ex: 5" />
        <InputField label="Pression (manomètre)" value={pression} onChange={setPression} unit="bar" placeholder="ex: 200" />
        <InputField label="Débit prescrit" value={debit} onChange={setDebit} unit="L/min" />
      </div>
      <ResultBox>
        <ResultLine label="Autonomie estimée" value={autonomieMin ? autonomieAffichee : null} unit="" highlight={autonomieMin !== null && autonomieMin < 30} />
      </ResultBox>
      {alerts.map((a, i) => <AlertVigilance key={i} message={a} />)}
      <MiniChat tabId="oxygene" />
    </div>
  );
}

// ─── Configuration des Onglets ────────────────────────
const TABS = [
  { id: 'perfusion', label: 'Perfusion',      icon: Droplets },
  { id: 'dose',      label: 'Dose',           icon: Pill },
  { id: 'pse',       label: 'PSE',            icon: Syringe },
  { id: 'clairance', label: 'Clairance (IR)', icon: Activity },
  { id: 'oxygene',   label: 'Autonomie O2',   icon: Wind },
];

// Mapping des labels stockés dans Firestore → id d'onglet
const LABEL_TO_TAB_ID = {
  'Débit de perfusion': 'perfusion',
  'Calcul de dose': 'dose',
  'Débit PSE': 'pse',
  'Clairance (Cockcroft-Gault)': 'clairance',
  'Autonomie O2': 'oxygene',
};

// ─── Page Principale ──────────────────────────────────
export default function CalculateursInfPage() {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState('perfusion');
  const [highlighted, setHighlighted] = useState(false);
  const calcRef = useRef(null);

  useEffect(() => {
    const name = location.state?.calculatorName;
    if (!name) return;
    const tabId = LABEL_TO_TAB_ID[name];
    if (!tabId) return;
    setActiveTab(tabId);
    setHighlighted(true);
    const scrollTimer = setTimeout(() => {
      calcRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
    const clearTimer = setTimeout(() => setHighlighted(false), 2100);
    return () => {
      clearTimeout(scrollTimer);
      clearTimeout(clearTimer);
    };
  }, []);

  return (
    <div className={`min-h-screen ${theme === 'dark' ? 'dark' : ''}`}>
      <div className="min-h-screen bg-slate-50 dark:bg-slate-950 transition-colors">

        {/* Header */}
        <header className="sticky top-0 z-10 bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800 shadow-sm">
          <div className="max-w-3xl mx-auto px-4 py-3 flex items-center gap-3">
            <button
              onClick={() => navigate('/pro/infirmier')}
              className="flex items-center gap-2 text-slate-500 dark:text-slate-400 hover:text-teal-600 dark:hover:text-teal-400 transition text-sm"
            >
              <ArrowLeft size={18} />
              Retour
            </button>
            <div className="h-5 w-px bg-slate-200 dark:bg-slate-700" />
            <h1 className="font-semibold text-slate-800 dark:text-slate-100 text-base">
              Calculateurs infirmiers
            </h1>
          </div>
        </header>

        {/* Contenu */}
        <main className="max-w-3xl mx-auto px-4 py-6 space-y-4">

          {/* Menu Onglets */}
          <div className="flex overflow-x-auto pb-2 -mx-4 px-4 sm:mx-0 sm:px-0 sm:flex-wrap gap-2 no-scrollbar">
            {TABS.map(tab => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition whitespace-nowrap ${
                    isActive
                      ? 'bg-teal-500 text-white shadow-md shadow-teal-500/20'
                      : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 border border-slate-200 dark:border-slate-700 hover:border-teal-400 dark:hover:border-teal-500 hover:text-teal-600'
                  }`}
                >
                  <Icon size={15} />
                  {tab.label}
                </button>
              );
            })}
          </div>

          {/* Calculateur actif */}
          <div
            ref={calcRef}
            className={`bg-white dark:bg-slate-900 rounded-2xl border shadow-sm p-5 sm:p-6 transition-all duration-300 ${
              highlighted
                ? 'border-teal-400 dark:border-teal-500 ring-2 ring-teal-400/40 dark:ring-teal-500/30 shadow-teal-400/20'
                : 'border-slate-200 dark:border-slate-800'
            }`}
          >
            {activeTab === 'perfusion' && <CalculateurPerfusion />}
            {activeTab === 'dose'      && <CalculateurDose />}
            {activeTab === 'pse'       && <CalculateurPSE />}
            {activeTab === 'clairance' && <CalculateurClairance />}
            {activeTab === 'oxygene'   && <CalculateurO2 />}
          </div>

          {/* Aide-mémoire */}
          <MemoCard tabId={activeTab} />

          <div className="flex items-start gap-2 pb-6 text-xs text-slate-400 dark:text-slate-500">
            <AlertTriangle size={14} className="mt-0.5 shrink-0 text-amber-400" />
            <span>
              Ces calculs sont indicatifs. Vérifiez toujours avec les protocoles de votre établissement et les prescriptions médicales.
            </span>
          </div>
        </main>
      </div>
    </div>
  );
}
